import { Box, Typography, IconButton } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import BottomNav, { DESKTOP_SIDEBAR_WIDTH, MOBILE_BOTTOM_CHROME } from './BottomNavigation';
import CompanionPanel, { DESKTOP_COMPANION_WIDTH } from './CompanionPanel';
import DesignSystem from '../../designSystem';
import { isFeatureEnabled } from '../../config/featureFlags';
import { useNewsUpdates } from '../../contexts/NewsUpdatesContext';
import { useT } from '../../i18n/useT';

export { IconTile } from './IconTile';

const WIDTHS = {
  sm: 560,
  md: 640,
  lg: 840,
  feed: 600,
} as const;

const UNCONSTRAINED = 1100;

interface AppShellProps {
  title?: string;
  children: ReactNode;
  showBackButton?: boolean;
  showSearch?: boolean;
  maxWidth?: 'sm' | 'md' | 'lg' | 'feed' | false;
  actions?: ReactNode;
  hideTitle?: boolean;
  /** Substack-style right discovery column (lg+). Default on for product pages. */
  showCompanion?: boolean;
  /** Hide sticky top header on desktop (Substack has none). Default true. */
  hideDesktopHeader?: boolean;
}

export default function AppShell({
  title,
  children,
  showBackButton = false,
  showSearch = false,
  maxWidth = 'feed',
  actions,
  hideTitle = false,
  showCompanion = true,
  hideDesktopHeader = true,
}: AppShellProps) {
  const t = useT();
  const navigate = useNavigate();
  const { count: notificationBadgeCount } = useNewsUpdates();
  const companionEnabled =
    showCompanion && isFeatureEnabled('webDesktopCompanionPanels');
  const contentMax = maxWidth === false ? UNCONSTRAINED : WIDTHS[maxWidth];

  return (
    <Box
      sx={{
        pb: { xs: `calc(${MOBILE_BOTTOM_CHROME}px + env(safe-area-inset-bottom, 0px))`, md: 0 },
        minHeight: '100dvh',
        // Desktop: page itself never scrolls — the centre column owns scrolling
        // so the fixed rail and companion column stay put.
        height: { xs: 'auto', md: '100dvh' },
        overflow: { xs: 'visible', md: 'hidden' },
        display: { xs: 'block', md: 'flex' },
        flexDirection: { md: 'column' },
        backgroundColor: DesignSystem.colors.canvas,
        color: DesignSystem.colors.textPrimary,
        pl: { xs: 0, md: `${DESKTOP_SIDEBAR_WIDTH}px` },
        pr: {
          xs: 0,
          lg: companionEnabled ? `${DESKTOP_COMPANION_WIDTH}px` : 0,
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          display: hideDesktopHeader ? { xs: 'block', md: 'none' } : 'block',
        }}
      >
        <Header
          title={t('brandName')}
          showSearch={showSearch}
          compact
          notificationBadgeCount={notificationBadgeCount}
        />
      </Box>

      <Box
        sx={{
          flex: { md: 1 },
          minHeight: { md: 0 },
          overflowY: { xs: 'visible', md: 'auto' },
          overscrollBehavior: 'contain',
          scrollbarWidth: { md: 'none' },
          '&::-webkit-scrollbar': {
            display: { md: 'none' },
          },
        }}
      >
        <Box
          component="main"
          sx={{
            width: '100%',
            maxWidth: contentMax,
            minWidth: 0,
            mx: 'auto',
            px: { xs: 2, md: 2.5 },
            py: { xs: 2, md: 2.5 },
            boxSizing: 'border-box',
            overflowX: 'hidden',
          }}
        >
          {!hideTitle && title && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                mb: DesignSystem.layout.sectionGap,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                {showBackButton && (
                  <IconButton
                    size="small"
                    onClick={() => navigate(-1)}
                    sx={{ color: DesignSystem.colors.textPrimary }}
                  >
                    <ArrowBack />
                  </IconButton>
                )}
                <Typography
                  component="h1"
                  sx={{
                    fontSize: DesignSystem.typography.pageTitle.fontSize,
                    fontWeight: DesignSystem.typography.pageTitle.fontWeight,
                    letterSpacing: DesignSystem.typography.pageTitle.letterSpacing,
                    color: DesignSystem.colors.textPrimary,
                    lineHeight: DesignSystem.typography.pageTitle.lineHeight,
                  }}
                >
                  {title}
                </Typography>
              </Box>
              {actions}
            </Box>
          )}
          {hideTitle && (showBackButton || actions) && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1.5,
              }}
            >
              {showBackButton ? (
                <IconButton
                  size="small"
                  onClick={() => navigate(-1)}
                  sx={{ color: DesignSystem.colors.textPrimary }}
                >
                  <ArrowBack />
                </IconButton>
              ) : (
                <span />
              )}
              {actions}
            </Box>
          )}
          {children}
        </Box>
      </Box>

      {companionEnabled && <CompanionPanel />}
      <BottomNav />
    </Box>
  );
}

export function SettingsGroup({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        bgcolor: DesignSystem.colors.surface,
        borderRadius: DesignSystem.borderRadius.lg,
        border: 0,
        overflow: 'hidden',
        mb: 1.5,
      }}
    >
      {children}
    </Box>
  );
}

export function SettingsGroupLabel({ children }: { children: ReactNode }) {
  return (
    <Typography
      sx={{
        m: 0,
        mb: 0.85,
        mt: 0.5,
        px: 0.25,
        fontSize: 12,
        fontWeight: 650,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: DesignSystem.colors.textSecondary,
      }}
    >
      {children}
    </Typography>
  );
}
